import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import MenuBar from './../components/Menubar';
import User from './../components/Utils';
import LoaderContext from './../components/LoaderContext';

type Props = {
  user: User;
  handleLogin(user: any): void;
};

type GiftCardGroup = {
  amount: number;
  csvEmails: string;
  count: number;
};

const AdminGiftCardSendListPage: React.FC<Props> = (props) => {
  const [error, setError] = useState('');
  const [groups, setGroups] = useState<GiftCardGroup[]>([]);
  const [showJumpToTopButton, setShowJumpToTopButton] = useState(false);

  const { showLoader, hideLoader } = useContext(LoaderContext);
  let didCancel = false;

  // Mirrors the same log helper pattern used across all admin pages
  const log = (message: any) => {
    if (process.env.REACT_APP_LOG === 'true') {
      console.log(message);
    }
  };

  const handleScroll = () => {
    const scrollThreshold = window.innerHeight * 1;
    setShowJumpToTopButton(window.scrollY > scrollThreshold);
  };

  const handleJumpToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (props.user.isLogged) {
      log('[GiftCardList] User is logged in — initiating data load. userId=' + props.user.id);
      loadData();
    } else {
      log('[GiftCardList] User is NOT logged in — skipping data load.');
    }
    return () => {
      didCancel = true;
    };
  }, [props.user.isLogged]);

  const handleErrors = (response: Response) => {
    log('[GiftCardList] HTTP response received — status=' + response.status + ' ok=' + response.ok);
    if (!response.ok) {
      const err = response.statusText || 'Error';
      log('[GiftCardList] HTTP error — ' + err);
      throw Error(err);
    }
    return response;
  };

  const loadData = () => {
    log('[GiftCardList] Fetching /rewards/admin/gift-card-list from server...');
    showLoader();
    fetch(process.env.REACT_APP_SERVER_URL + '/rewards/admin/gift-card-list?v=' + Date.now(), {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: 'Bearer ' + props.user.token,
      }),
    })
      .then(handleErrors)
      .then((res) => res.json())
      .then(
        (result: GiftCardGroup[]) => {
          log('[GiftCardList] Data received — ' + result.length + ' amount group(s)');
          result.forEach((group) => {
            log('[GiftCardList] Group $' + group.amount + ' — ' + group.count + ' recipient(s) — emails: ' + group.csvEmails);
          });

          if (result.length === 0) {
            log('[GiftCardList] No reward groups returned — either no qualifying users or all transactions are reversed.');
          }

          if (!didCancel) {
            hideLoader();
            setGroups(result);
            setError('');
          }
        },
        (err) => {
          log('[GiftCardList] Fetch error — ' + err.message);
          if (!didCancel) {
            hideLoader();
            setError(err.message);
          }
        }
      );
  };

  const isAdmin =
    props.user.isLogged &&
    (props.user.id === 6 || props.user.id === 7 || props.user.id === 514);

  if (!isAdmin) {
    return (
      <div>
        <div>Access denied.</div>
      </div>
    );
  }

  return (
    <main>
      <div className="container mypage">
        <section>
          <MenuBar user={props.user} />
          <div className="page-box-main">
            {/* Page heading — consistent with AdminCashRewardPage pattern */}
            <h2 className="futuralt-book pt-2 mb-4 text-center f-22">
              <button className="next-button">Gift Card Send List</button>
            </h2>

            {error !== '' && <p style={{ color: 'red' }}>{error}</p>}

            {groups.length === 0 && error === '' && (
              <p className="text-center">No reward recipients found.</p>
            )}

            {/*
             * One section per dollar amount, sorted cheapest-to-highest (server sorts).
             * Under each heading: the raw CSV string, selectable for copy-paste.
             * Larry pastes directly into Amazon's gift-card send form — no buttons needed.
             * IMPORTANT: only APPLIED (non-reversed) transactions are included.
             */}
            {groups.map((group) => (
              <div key={group.amount} style={{ marginBottom: '32px' }}>
                {/* Amount heading */}
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    borderBottom: '1px solid #ccc',
                    paddingBottom: '4px',
                  }}
                >
                  ${group.amount} &mdash; {group.count} recipient{group.count !== 1 ? 's' : ''}
                </h3>

                {/* CSV email list — plain text, easy to select & copy */}
                <p
                  style={{
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    background: '#f9f9f9',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '12px',
                    margin: 0,
                    userSelect: 'text',
                  }}
                >
                  {group.csvEmails}
                </p>
              </div>
            ))}

            <div style={{ marginTop: '24px' }}>
              <button className="next-button black">
                <Link to="/admin">Back</Link>
              </button>
            </div>
          </div>
        </section>
      </div>

      {showJumpToTopButton && (
        <button className="jump-to-top-button" onClick={handleJumpToTop}>
          Jump to Top
        </button>
      )}
    </main>
  );
};

export default AdminGiftCardSendListPage;
